import { ROUTES } from './constants';
import {
  BUSINESS_CALENDAR_APP_ID,
  POS_APP_ID,
  TAX_CALENDAR_APP_ID,
} from './installedAppPathScope';

export const EMAIL_NOTIFICATION_SETTING_KEYS = [
  'emailTaxCalendarDeadlines',
  'emailBusinessCalendarUrgency',
  'emailInventoryLowStock',
] as const;

export type EmailNotificationSettingKey =
  (typeof EMAIL_NOTIFICATION_SETTING_KEYS)[number];

export type EmailNotificationPreferencePatch = Partial<
  Record<EmailNotificationSettingKey, boolean>
>;

/**
 * Inventory low stock is the POS inventory feature. There is no separate
 * Inventory catalog app; stock lives under the POS app at `/pos/inventory`.
 */
export const EMAIL_NOTIFICATION_SETTING_ACCESS: Record<
  EmailNotificationSettingKey,
  { appId: string; accessPath: string }
> = {
  emailTaxCalendarDeadlines: {
    appId: TAX_CALENDAR_APP_ID,
    accessPath: ROUTES.TAX_COMPLIANCE,
  },
  emailBusinessCalendarUrgency: {
    appId: BUSINESS_CALENDAR_APP_ID,
    accessPath: ROUTES.BUSINESS_CALENDAR,
  },
  emailInventoryLowStock: {
    appId: POS_APP_ID,
    accessPath: ROUTES.POS.INVENTORY,
  },
};

export type InstalledAppsLoadStatus = 'idle' | 'loading' | 'success' | 'error';

export type EmailNotificationSettingsVisibility =
  | { status: 'pending' }
  | { status: 'error'; message: string | null }
  | { status: 'ready'; enabledKeys: EmailNotificationSettingKey[] };

export function resolveEmailNotificationSettingsVisibility(input: {
  companyInitialized: boolean;
  hasScopedCompany: boolean;
  permissionsReady: boolean;
  installedAppsStatus: InstalledAppsLoadStatus;
  installedAppsCompanyMatches: boolean;
  loadError: string | null;
  isAppInstalled: (appId: string) => boolean;
  canAccessPath: (path: string) => boolean;
}): EmailNotificationSettingsVisibility {
  if (!input.companyInitialized) {
    return { status: 'pending' };
  }

  // All-companies mode and a missing company have no single company whose
  // installed apps can enable these settings.
  if (!input.hasScopedCompany) {
    return { status: 'ready', enabledKeys: [] };
  }

  if (!input.permissionsReady) {
    return { status: 'pending' };
  }

  if (
    input.installedAppsStatus === 'idle' ||
    input.installedAppsStatus === 'loading' ||
    !input.installedAppsCompanyMatches
  ) {
    return { status: 'pending' };
  }

  if (input.installedAppsStatus === 'error') {
    return {
      status: 'error',
      message: input.loadError?.trim() || null,
    };
  }

  const enabledKeys = EMAIL_NOTIFICATION_SETTING_KEYS.filter((key) => {
    const access = EMAIL_NOTIFICATION_SETTING_ACCESS[key];
    return (
      input.isAppInstalled(access.appId) &&
      input.canAccessPath(access.accessPath)
    );
  });

  return { status: 'ready', enabledKeys };
}

/** Sidebar and command palette keep the entry while apps or permissions load. */
export function shouldShowNotificationSettingsEntry(
  visibility: EmailNotificationSettingsVisibility,
): boolean {
  if (visibility.status !== 'ready') {
    return true;
  }
  return visibility.enabledKeys.length > 0;
}

/** The notifications-page button appears only when at least one setting applies. */
export function shouldShowNotificationSettingsButton(
  visibility: EmailNotificationSettingsVisibility,
): boolean {
  return visibility.status === 'ready' && visibility.enabledKeys.length > 0;
}

export function buildEmailNotificationGroupPatch(
  enabledKeys: readonly EmailNotificationSettingKey[],
  enabled: boolean,
): EmailNotificationPreferencePatch {
  const patch: EmailNotificationPreferencePatch = {};
  for (const key of enabledKeys) {
    patch[key] = enabled;
  }
  return patch;
}
